import { useCallback, useState, useRef, useEffect } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useMsal } from '@azure/msal-react';
import { LogOut, User, Menu, X, HelpCircle, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { cn } from '@/utils/cn';
import { APP_NAME } from '@/utils/constants';

function focusIndexedItem<T extends HTMLElement>(items: Array<T | null>, index: number) {
  items[index]?.focus();
}

function getKeyboardTargetIndex<T extends HTMLElement>(
  event: ReactKeyboardEvent,
  items: Array<T | null>,
): number | null {
  const availableItems = items.filter((item): item is T => item !== null);
  if (availableItems.length === 0) return null;
  const currentIndex = availableItems.findIndex((item) => item === document.activeElement);

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    return currentIndex >= 0 ? (currentIndex + 1) % availableItems.length : 0;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    return currentIndex >= 0
      ? (currentIndex - 1 + availableItems.length) % availableItems.length
      : availableItems.length - 1;
  }

  if (event.key === 'Home') {
    event.preventDefault();
    return 0;
  }

  if (event.key === 'End') {
    event.preventDefault();
    return availableItems.length - 1;
  }

  return null;
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { accounts, instance } = useMsal();
  const userName = accounts[0]?.name ?? '';
  const userEmail = accounts[0]?.username ?? '';

  const supportRef = useRef<HTMLDivElement>(null);
  const supportButtonRef = useRef<HTMLButtonElement>(null);
  const supportItemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const userRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const userItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const closeSupportMenu = useCallback(
    ({ restoreFocus = false }: { restoreFocus?: boolean } = {}) => {
      setSupportMenuOpen(false);
      if (restoreFocus) {
        requestAnimationFrame(() => supportButtonRef.current?.focus());
      }
    },
    [],
  );

  const closeUserMenu = useCallback(
    ({ restoreFocus = false }: { restoreFocus?: boolean } = {}) => {
      setUserMenuOpen(false);
      if (restoreFocus) {
        requestAnimationFrame(() => userButtonRef.current?.focus());
      }
    },
    [],
  );

  // Close support and user dropdowns on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (supportRef.current && !supportRef.current.contains(e.target as Node)) {
        closeSupportMenu();
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        closeUserMenu();
      }
    }
    if (supportMenuOpen || userMenuOpen) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [supportMenuOpen, userMenuOpen, closeSupportMenu, closeUserMenu]);

  // Close open dropdowns on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (supportMenuOpen) closeSupportMenu({ restoreFocus: true });
      if (userMenuOpen) closeUserMenu({ restoreFocus: true });
    }
    if (supportMenuOpen || userMenuOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [supportMenuOpen, userMenuOpen, closeSupportMenu, closeUserMenu]);

  return (
    <>
      <header className="flex h-12 items-center bg-black w-full relative z-50">
        {/* Mobile menu toggle */}
        <button
          className="flex md:hidden w-10 h-10 items-center justify-center text-white ml-1"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          type="button"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <span className="px-4 text-sm font-bold text-white whitespace-nowrap">Deloitte</span>
        <div className="w-px h-4 bg-cool-gray-11 hidden md:block" />
        <div className="hidden md:flex items-center gap-4 ml-4">
          <span className="text-sm font-semibold text-white whitespace-nowrap">{APP_NAME}</span>
        </div>
        <div className="flex items-center gap-4 sm:gap-8 ml-auto pr-1">
          {/* Support / Have a Question */}
          <div ref={supportRef} className="relative flex items-center">
            <button
              ref={supportButtonRef}
              type="button"
              className="w-10 h-10 bg-black border-none flex items-center justify-center text-cool-gray-7 hover:text-white"
              title="Have a Question"
              aria-haspopup="menu"
              aria-expanded={supportMenuOpen}
              aria-label="Support"
              onClick={() => {
                closeUserMenu();
                setSupportMenuOpen((v) => !v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSupportMenuOpen(true);
                  requestAnimationFrame(() => focusIndexedItem(supportItemRefs.current, 0));
                }
              }}
            >
              <HelpCircle size={20} />
            </button>

            {supportMenuOpen && (
              <ul
                role="menu"
                aria-label="Support options"
                className="absolute right-0 top-full mt-1 min-w-[14rem] bg-white border border-cool-gray-2 shadow-lg rounded z-50"
                onKeyDown={(e) => {
                  const index = getKeyboardTargetIndex(e, supportItemRefs.current);
                  if (index !== null) focusIndexedItem(supportItemRefs.current, index);
                }}
              >
                <li role="none">
                  <a
                    ref={(element) => {
                      supportItemRefs.current[0] = element;
                    }}
                    role="menuitem"
                    href="https://deloitteus.service-now.com/sp?id=sc_cat_item&sys_id=288623ba4fd8ef00f7a9cf5d0210c7b6&table=sc_cat_item"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2.5 text-sm text-black hover:bg-cool-gray-2 transition-colors"
                    onClick={() => closeSupportMenu()}
                  >
                    Raise an Incident
                  </a>
                </li>
                <li role="none">
                  <a
                    ref={(element) => {
                      supportItemRefs.current[1] = element;
                    }}
                    role="menuitem"
                    href="https://deloitteus.service-now.com/sp?id=sc_cat_item&sys_id=e204aca34fe5a740f7a9cf5d0210c779&table=sc_cat_item"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2.5 text-sm text-black hover:bg-cool-gray-2 transition-colors"
                    onClick={() => closeSupportMenu()}
                  >
                    Submit a Request
                  </a>
                </li>
              </ul>
            )}
          </div>

          <div ref={userRef} className="relative flex items-center">
            <button
              ref={userButtonRef}
              type="button"
              className={cn(
                'h-10 px-2 bg-black border-none flex items-center gap-2 text-cool-gray-7 hover:text-white',
              )}
              aria-label="User menu"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={() => {
                closeSupportMenu();
                setUserMenuOpen((v) => !v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setUserMenuOpen(true);
                  requestAnimationFrame(() => focusIndexedItem(userItemRefs.current, 0));
                }
              }}
            >
              <div className="flex items-center gap-2 text-sm text-cool-gray-7">
                <div className="w-8 h-8 rounded-full bg-cool-gray-11 flex items-center justify-center">
                  <User size={16} className="text-white" />
                </div>
                {userName && <span className="hidden lg:inline text-white text-sm">{userName}</span>}
              </div>
              {userMenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {userMenuOpen && (
              <ul
                role="menu"
                aria-label="User options"
                className="absolute right-0 top-full mt-1 min-w-[16rem] bg-white border border-cool-gray-2 shadow-lg rounded z-50"
                onKeyDown={(e) => {
                  const index = getKeyboardTargetIndex(e, userItemRefs.current);
                  if (index !== null) focusIndexedItem(userItemRefs.current, index);
                }}
              >
                <li role="none" className="px-4 py-3 border-b border-cool-gray-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cool-gray-11 flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-black truncate">{userName || 'User'}</div>
                      {userEmail && <div className="text-xs text-cool-gray-7 truncate">{userEmail}</div>}
                    </div>
                  </div>
                </li>
                <li role="none">
                  <button
                    ref={(element) => {
                      userItemRefs.current[0] = element;
                    }}
                    type="button"
                    role="menuitem"
                    className="w-full px-4 py-2.5 text-sm text-left text-black hover:bg-cool-gray-2 transition-colors flex items-center gap-2"
                    onClick={() => closeUserMenu()}
                  >
                    <Settings size={14} />
                    Preferences
                  </button>
                </li>
                <li role="none" className="border-t border-cool-gray-2" />
                <li role="none">
                  <button
                    ref={(element) => {
                      userItemRefs.current[1] = element;
                    }}
                    type="button"
                    role="menuitem"
                    className="w-full px-4 py-2.5 text-sm text-left text-black hover:bg-cool-gray-2 transition-colors flex items-center gap-2"
                    onClick={() => {
                      closeUserMenu();
                      void instance.logoutRedirect();
                    }}
                  >
                    <LogOut size={14} />
                    Log Out
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </header>


    </>
  );
}
